import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { INoteType } from '../service';
import { WoundNoteType } from '@app/core/enumeration';
import { CarePathwayAssignedType, ReferenceByEntity, TissueType } from './reference-by-entity.model';
import { ProductLineDataForCascadingInterface } from './product-line-data-cascading.interface';

export interface INoteData {
  medicalHistory?: any;
  isServiceLineView?: boolean;
  noteCategory: string;
  noteList?: any[];
  noteTypeList?: any[];
  productName?: string;
  referenceId: string;
  selectedServiceDetail?: any;
  serviceLineSequenceNumber?: number;
  serviceNumber?: number;
  otherPLs?: ProductLineDataForCascadingInterface[];
  lineOfBusiness?: string;
  tenantName?: string;
  authorizationStatus?: string;
  productSequenceNumber?: number;
  subNoteCategory?: string;
  authorizationNumber?: string;
}

export interface NoteConfig {
  noteTypes: string[];
  referenceId?: string | number;
}

export interface NoteSidebarData {
  type: string | undefined;
  payload: INoteData;
}

export interface INoteBaseData {
  createdBy: string;
  healthPlan: string;
  memberId: string;
  memberName: string;
  modifiedBy: string;
  noteCategory: string;
  noteCategoryName: string;
  providerName: string;
  providerType: string;
  referenceId: string;
  subscriberNumber: string;
  tenantId: string;

  approvedVisits?: number;
  authorizationId?: string;
  authorizationNumber?: string;
  centralAuthDeptFax?: string;
  certifiedPeriod?: number;
  discipline?: string;
  disciplines?: string;
  effectiveFrom?: string | Date;
  effectiveTo?: string | Date;
  icdCode?: string;
  orderingProviderAddress?: string;
  orderingProviderFax?: string;
  orderingProviderNPI?: string;
  orderingProviderName?: string;
  orderingProviderPhone?: string;
  originalVisitsRequested?: string;
  parentCompany?: string;
  primaryDiagnosis?: string;
  providerAddress?: string;
  providerCentralAuthFax?: string;
  providerCentralAuthPhone?: string;
  providerFax?: string;
  providerNPI?: string;
  providerParentCompany?: string;
  providerPhone?: string;
  referenceClue?: string;
  serviceLineId?: string;
  serviceLineSequenceNumber?: number;
  serviceRequestType?: string;
  socDate?: string | Date;
  startOfCare?: string | Date;
  startOfCareDate?: string | Date;
  subType?: string;
  typeOfProvider?: string;
  visitsSaved?: string;
}

export interface Note {
  patientName?: string;
  serviceLineSequenceNumber?: number;
  noteId?: string;
  tenantId?: string;
  tenantName?: string;
  noteTypeId?: string;
  noteType?: string;
  noteCategoryId?: string;
  noteCategoryName?: string;
  referenceClue?: string;
  noteComment?: string;
  memberId?: string;
  providerId?: number;
  serviceLineId?: string;
  servicingProviderName?: string;
  authorizationId?: string;
  orderingPhysicianId?: string;
  followingPhysicianId?: string;
  orderingFacilityId?: string;
  healthPlan?: string;
  hcpcSkill?: string;
  comment?: string;
  createdOn?: Date | string;
  createdBy?: string;
  modifiedOn?: Date | string;
  modifiedBy?: string;
  dateandtime?: string;
  disciplineDescription?: string;
  isReconsideration?: boolean;
  contacts?: Array<Contact>;
  isCreatedByExternal?: boolean;
  userName?: string;
  lineOfBusinessName?: string;
}

export interface NoteDialogItem {
  noteId: string;
  active: boolean;
  model: Note;
  item: string | INoteType;
  category: string;
  isEditable: boolean;
}

export interface INoteForm {
  referenceId: FormControl;
  referenceClue: FormControl;
  noteCategoryName: FormControl;
  memberName: FormControl;
  subscriberNumber: FormControl;
  memberId: FormControl;
  authorizationId: FormControl;
  authorizationNumber: FormControl;
  serviceLineSequenceNumber: FormControl;
  serviceRequestType: FormControl;
  serviceLineId: FormControl;
  healthPlan: FormControl;
  providerType: FormControl;
  providerName: FormControl;
  noteType: FormControl;
  tenantId: FormControl;
  noteId?: FormControl;

  createdBy: FormControl;
  createdOn: FormControl;
  modifiedOn: FormControl;
  modifiedBy: FormControl;
}

export class Contact {
  contactName?: string = '';
  contactTitle?: string = '';
  contactType?: string | string[] = '';
  otherContactType?: string = '';
  contactPhone?: string = '';
  contactEmail?: string = '';
  noteContactId?: string = '';
  communicationType?: string = '';
  extension?: string = '';
  isReconsideration?= false;
}

export interface DenialExplanation extends Note {
  aorPresent?: boolean;
  rationaleDiscussed?: boolean;
  clericalError?: boolean;
  selectNextStep?: string;
  educationCaller?: boolean;
}


export interface LocationNote {
  createdBy: string,
  createdDate: string,
  isOldData: boolean,
  location: string,
  locationNoteId: string,
  memberId: string,
  modifiedBy: string,
  modifiedDate: string
}

export interface IWoundReview extends Note {
  authorizationNumber: string;
  certifiedPeriod?: number;
  describePatientLivingSituation?: string;
  discipline: string;
  effectiveFrom: Date | string;
  effectiveTo: Date | string;
  historyOfPresentIllness: string;
  locationOfMdOrder: string;
  memberName: string;
  orderedFrequency: string;
  pastMedicalHistory: string;
  primaryDiagnosis: string;
  providerName: string;
  providerType: string;
  referenceClue: string;
  referenceId: string;
  remainingVisitsInFrequencyToDate?: string;
  requestedUnits?: string;
  serviceLineSequenceNumber: number;
  serviceRequestType: string;
  startOfCare: Date | string;
  subType: string;
  subscriberNumber: string;
  orderingProvider?: string;
  reference: string;
  clinicalCriteriaHierarchyStatement: string;
  woundDocumentations: any[];
  ostomyDocumentations: OstomyDocumentation[];
  sourceOfData?: string;
  teleconsultationCompleted?: boolean;
  careConferenceDate?: Date | string;
  clinicalRoundsDate?: Date | string;
  woundSuggestion: ReferenceByEntity;
  woundClinicalRationale: string;
  specialInstructions: string;
  version?: string;
  gender?: string;
  age?: string;
  requestedVisits?: string;
  urgency?: string;
  certPeriod?: number;
  certDates?: string;
  certPeriodEffectiveFromDate?: Date | string;
  certPeriodEffectiveToDate?: Date | string;
  patientsLivingSituation?: string;
  patientsLivingSituationOther?: string;
  orderingPhysicianName?: string;
  engagementType?: string;
  notUtilizingCompression?: boolean;
  notUtilizingCompressionComment?: string;
  currentlyUtilizingCompression?: boolean;
  currentlyUtilizingCompressionComment?: string;
  longTermCompressionBeingAddressed?: boolean;
  longTermCompressionBeingAddressedComment?: string;
  noCompressionDTUnderlyingDiseaseProcess?: boolean;
  noCompression?: boolean;
  offloadingInterventionsDME: boolean;
  offloadingInterventionsDMEComment: string;
}


export interface WoundHealingRate {
  areaType: string;
  areaValue: number;
  woundDocumentationId: string;
  woundHealingRateId: string;
}

export enum WoundLocationStatus {
  Healed = 'Healed',
  Active = 'Active',
  Reactive = 'Reactive',
  Inactive = 'Inactive',
}

export enum WoundLocationStatusDropDown {
  Healed = 'Healed',
  Active = 'Active',
  Inactivated = 'Inactivated',
  Reactivated = 'Reactivated'
}


export interface WoundTrackerEntity {
  createdDate: string;
  dateOfMeasurements: string;
  dateOfMeasurementsModifiedDate: string;
  drainageAmount: ReferenceByEntity;
  drainageType: ReferenceByEntity;
  imageProvided: boolean;
  noteType: WoundNoteType;
  onsetDate: string;
  portalNoteId: string;
  sourceOfData: string; // enum
  teleconsultationCompleted: boolean;
  tissueTypes: TissueType[];
  tunnelings: WoundTunneling[];
  underminings: WoundUndermining[];
  woundHealingRates: WoundHealingRate[];
  woundDepth: number;
  woundHealedDate: string;
  woundInactivatedDate: string;
  woundLength: number;
  locationNote: string;
  locationNoteId: string;
  woundReactivatedDate: string;
  woundType: ReferenceByEntity;
  woundWidth: number;
  chronic: boolean;
  originallyChronic: boolean;
  woundTypeId?: string;
  woundCare?: string;
  previousWoundCare?: string;
}

export interface WoundLatestDetails extends WoundTrackerEntity {
  woundSurfaceArea: string;
  pain: ReferenceByEntity;
  woundStatus: ReferenceByEntity;
  woundCare: string;
  previousWoundCare: string;
  woundTypeComment: string;
  woundStatusComment: string;
  drainageTypeComment: string;
  drainageAmountComment: string;
  tissueTypeComment: string;
  comments?: WoundNoteComment[];
  drainageTypeId?: string;
  drainageAmountId?: string;
  tissueTypeIds?: string;
  painId?: string;
  woundStatusId?: string;
}

export interface IWoundDocumentationNote extends Note {
  noteType: string;
  memberName: string;
  memberId: string;
  referenceId: string;
  healthPlan: string;
  noteCategoryName: string,
  noteId: string,
  referenceClue: string,
  tenantId: string,
  woundDocumentations: IWoundDocumentationDetails[];
  createdBy?: string;
  createdDate?: string;
  modifiedDate?: string;
  modifiedBy?: string;
  engagementType: string,
  careConferenceDate: string,
  clinicalRoundsDate: string,
}

export interface IWoundDocumentationDetails extends IWoundDocumentation {
  woundImageUploadedDate: string,
  teleconsultationCompleted: boolean,
  drainageAmountComment: string,
  drainageTypeComment: string,
  woundTypeComment: string
  comments: WoundNoteComment[];
}

// one to many for wound review
export interface IWoundDocumentation {
  comment: string;
  drainageAmount: ReferenceByEntity;
  drainageType: ReferenceByEntity;
  measurementLengthWidthDepth: string;
  woundLength: number | string | null;
  woundWidth: number | string | null;
  woundDepth: number | string | null;
  woundSurfaceArea: number | string | null;
  // Wound Type Comment field
  otherText1: string;
  // Drainage Type Comments field
  otherText2: string;
  // Drainage Amount Comments field
  otherText3: string;
  tissueTypes: TissueType[];
  tissueTypeComments: string;
  woundCare: string;
  previousWoundCare: string;
  // woundLocation?: LocationNote;
  locationNote: string;
  locationNoteId: string;
  comments: WoundNoteComment[];
  onsetDate: Date | string | null;
  imageProvided: boolean;
  chronic: boolean;
  originallyChronic: boolean | undefined;
  teleconsultationCompleted: boolean;
  woundType: ReferenceByEntity;
  dateOfMeasurements: Date | string | null;
  isWoundHealed: boolean | null;
  woundHealedDate: Date | string | null;
  isWoundReactivated: boolean | null;
  woundReactivatedDate: Date | string | null;
  woundInactivated: boolean | null;
  woundInactivatedDate: Date | string | null;
  tunnelings: WoundTunneling[];
  underminings: WoundUndermining[];
  woundHealingRates: WoundHealingRate[];
  woundDocumentationId?: string;
  woundTypeCommentId: string,
  drainageTypeCommentId: string,
  drainageAmountCommentId: string,
  tissueTypeCommentId: string,
  commentId: string;
  woundImageUploadedDate: Date | string | null,
  careConferenceDate: Date | string | null,
  clinicalRoundsDate: Date | string | null,
}

export interface IWoundCareConnect extends Note {
  noteType: string;
  memberName: string;
  memberId: string;
  referenceId: string;
  healthPlan: string;
  providerType: string;
  providerName: string;
  agencyClinician: string;
  agencyClinicianContact: string;
  orderingProviderName: string;
  orderingProviderContact: string;
  woundDocumentations: WoundCareConnectDoc[];
  ostomyDocumentations: OstomyDocumentation[];
  woundCareConnectNoteInformation: WoundCareConnectInfo;
  createdBy?: string;
  createdDate?: string;
  modifiedDate?: string;
  modifiedBy?: string;
  sourceOfData?: string;
  noUtilizingCompression?: boolean;
  noUtilizingCompressionComment?: string;
  currentlyUtilizingCompression?: boolean;
  currentlyUtilizingCompressionComment?: string;
  longTermCompressionBeingAddressed?: boolean;
  longTermCompressionBeingAddressedComment?: string;
  noCompressionDTUnderlyingDiseaseProcess?: boolean;
  noCompression?: boolean;
}

export interface WoundCareConnectDoc {
  locationNote: string,
  locationNoteId: string,
  isWoundHealed: boolean,
  woundHealedDate: string | Date,
  isWoundReactivated: boolean,
  woundReactivatedDate: string | Date,
  woundInactivated: boolean,
  woundInactivatedDate: string | Date,
  onsetDate: string | Date,
  chronic: boolean,
  originallyChronic: boolean | undefined,
  woundType: ReferenceByEntity,
  woundTypeComment: string,
  drainageType: ReferenceByEntity,
  drainageTypeComment: string,
  drainageAmount: ReferenceByEntity,
  drainageAmountComment: string,
  tissueTypes: TissueType[],
  tissueTypeComments: string,
  comments: WoundNoteComment[],
  measurementLengthWidthDepth: string;
  tunnelings: WoundTunneling[],
  underminings: WoundUndermining[],
  dateOfMeasurements: string | Date,
  woundStatus: ReferenceByEntity,
  woundStatusComment: string,
  woundStatusCommentId: string,
  pain: ReferenceByEntity,
  woundCare: string,
  previousWoundCare: string,
  // fe form fields to concat into measurementLengthWidthDepth
  woundLength: string,
  woundWidth: string,
  woundDepth: string,
  woundSurfaceArea: string,
  woundDocumentationId?: string,
  woundTypeCommentId: string,
  drainageTypeCommentId: string,
  drainageAmountCommentId: string,
  tissueTypeCommentId: string,
}

export interface WoundNoteComment {
  woundDocumentationCommentId: string;
  woundDocumentationId: string;
  comment: string;
  type: string;
}

export interface WoundCareConnectInfo {
  noteType: string;
  engagementType: string;
  careConferenceType: string;
  careConferenceDate: string;
  communicationType: string;
  clinicalRoundsDate: string;
  isImageOrDocumentationUploaded: string;
  imageOrDocumentationUploadedDate: string;
  hospitalizationsSinceLastEngagement: WoundHospitalLE[];
  eRVisitsSinceLastEngagement: WoundErLE[];
  coMorbiditiesImpedingWoundHealing: string[];
  coMorbiditiesImpedingWoundHealingAdditionalText: string;
  isHgbA1C: boolean;
  hgbA1CInput: string;
  hgbA1CDate: string;
  isWoundCulture: boolean;
  woundCultureValue: string;
  woundCultureDate: string;
  isPrealbumin: boolean;
  prealbuminInput: string;
  prealbuminDate: string;
  isRecentBloodGlucose: boolean;
  recentBloodGlucoseInput: string;
  recentBloodGlucoseDate: string;
  isLabsOrStudiesOther: boolean;
  labsOrStudiesFreeText: string;
  topicsDiscussedDuringEngagement: string[];
  TopicsDiscussedComment: string;
  topicsDiscussedDuringEngagementAdditionalText: string;
  carePathwayAssigned: CarePathwayAssignedType[];
  isEducationProvided: string;
  educationProvided: string[];
  educationProvidedFreeText: string;
  narrativeNote: string;
  comment: string;
  caregiverStatus: string;
  caregiverStatusFreeText: string;
  offloadingInterventionsDME: boolean;
  offloadingInterventionsDMEComment: string;
  woundCultureDetails: string;
}

export interface WoundUndermining {
  measurement: number | string | null;
  fromLocation: Date | string | null;
  toLocation: Date | string | null;
  underminingId: string;
  woundDocumentationId: string;
}

export interface WoundTunneling {
  measurement: number | string | null;
  location: Date | string | null;
  tunnelingId: string;
  woundDocumentationId: string;
}

export interface IWoundReviewNoteForm extends INoteForm {
  version: FormControl;
  subType: FormControl;
  primaryDiagnosis: FormControl;
  comment: FormControl;
  discipline: FormControl;
  requestedUnits?: FormControl;
  effectiveFrom: FormControl;
  effectiveTo: FormControl;
  startOfCare: FormControl;
  certifiedPeriod?: FormControl;
  locationOfMdOrder: FormControl;
  orderingProvider?: FormControl;
  orderedFrequency: FormControl;
  previousOrders?: FormControl;
  remainingVisitsInFrequencyToDate: FormControl;
  remainingVisitsInFrequencyToDateUnknown: FormControl;
  historyOfPresentIllness: FormControl;
  pastMedicalHistory: FormControl;
  describePatientLivingSituation?: FormControl;
  describePatientLivingSituationComment?: FormControl;
  reference: FormControl;
  medicaidReference?: FormControl;
  clinicalCriteriaHierarchyStatement?: FormControl;
  teleconsultationCompleted?: FormControl;
  engagementType?: FormControl;
  careConferenceDate?: FormControl;
  clinicalRoundsDate?: FormControl;
  woundDocumentations: FormArray;
  ostomyDocumentations: FormArray;
  woundSuggestion: FormControl;
  woundClinicalRationale: FormControl;
  specialInstructions: FormControl;
  notUtilizingCompression?: FormControl;
  notUtilizingCompressionComment?: FormControl;
  currentlyUtilizingCompression?: FormControl;
  currentlyUtilizingCompressionComment?: FormControl;
  longTermCompressionBeingAddressed?: FormControl;
  longTermCompressionBeingAddressedComment?: FormControl;
  noCompressionDTUnderlyingDiseaseProcess?: FormControl;
  noCompression?: FormControl;
  gender?: FormControl;
  age?: FormControl;
  requestedVisits?: FormControl;
  urgency?: FormControl;
  certPeriod?: FormControl;
  certDates?: FormControl;
  certPeriodEffectiveFromDate?: FormControl;
  certPeriodEffectiveToDate?: FormControl;
  patientsLivingSituation?: FormControl;
  patientsLivingSituationOther?: FormControl;
  availabilityOfAssistance? : FormControl;
  orderingPhysicianName?: FormControl;
  noteTypeId?: FormControl;
  tenantName?: FormControl;
  serviceStatus?: FormControl;
  cascadingServiceLineIds?: FormControl;
  offloadingInterventionsDME: FormControl;
  offloadingInterventionsDMEComment: FormControl;
}

export interface IUrgentNoteForm extends INoteForm {
  comment: FormControl;
  contacts: FormArray;
}

export interface IWoundDocumentNoteForm {
  locationNote: FormControl;
  onsetDate: FormControl;
  chronic: FormControl;
  originallyChronic: FormControl;
  woundType: FormControl;
  drainageType: FormControl;
  drainageAmount: FormControl;
  tissueTypes: FormControl;
  woundCare: FormControl;
  previousWoundCare: FormControl;
  tissueTypeComments: FormControl;
  measurementLengthWidthDepth: FormControl;
  woundLength: FormControl;
  woundWidth: FormControl;
  woundDepth: FormControl;
  woundSurfaceArea: FormControl;
  // Wound Type Comment field
  otherText1: FormControl;
  // Drainage Type Comments field
  otherText2: FormControl;
  // Drainage Amount Comments field
  otherText3: FormControl;
  comment: FormControl;
  dateOfMeasurements: FormControl;
  isWoundHealed: FormControl;
  woundHealedDate: FormControl;
  isWoundReactivated: FormControl;
  woundReactivatedDate: FormControl;
  woundInactivated: FormControl;
  woundInactivatedDate: FormControl;
  tunnelings: FormArray;
  underminings: FormArray;
  pain: FormControl;
  woundDocumentationId?: FormControl;
  woundTypeCommentId: FormControl,
  drainageTypeCommentId: FormControl,
  drainageAmountCommentId: FormControl,
  tissueTypeCommentId: FormControl,
  commentId: FormControl;
}

export interface IWoundDocumentForm {
  locationNote: FormControl,
  isWoundHealed: FormControl,
  woundHealedDate: FormControl,
  isWoundReactivated: FormControl,
  woundReactivatedDate: FormControl,
  woundInactivated: FormControl,
  woundInactivatedDate: FormControl,
  onsetDate: FormControl,
  woundType: FormControl,
  drainageType: FormControl,
  drainageAmount: FormControl,
  tissueTypes: FormControl,
  tissueTypeComments: FormControl,
  woundTypeComment: FormControl,
  drainageTypeComment: FormControl,
  drainageAmountComment: FormControl,
  imageProvided: FormControl,
  woundImageUploadedDate: FormControl,
  measurementLengthWidthDepth: FormControl,
  woundLength: FormControl,
  woundWidth: FormControl,
  woundDepth: FormControl,
  woundSurfaceArea: FormControl,
  tunnelings: FormArray,
  underminings: FormArray,
  dateOfMeasurements: FormControl,
  teleconsultationCompleted: FormControl,
  engagementType: FormControl,
  careConferenceDate: FormControl,
  clinicalRoundsDate: FormControl,
  woundDocumentationId?: FormControl,
  woundTypeCommentId: FormControl,
  drainageTypeCommentId: FormControl,
  drainageAmountCommentId: FormControl,
  tissueTypeCommentId: FormControl,
  previousWoundCare: FormControl,
}

export interface CaseConference extends Note {
  careConferenceType?: string;
  department?: string;
  outreachAttempt?: string;
  result?: string;
  timeSpentOnCall?: string;
}

interface FollowUpActionsInitiated {
  requestAddInfo?: boolean;
  provideRequestedInfo?: boolean;
  forwardMisdirectedAOrG?: boolean;
  notificationOfOutcomeOfAOrG?: boolean;
  pcpByMyNexusMedicalDirector?: boolean;
  healthPlanCmReferral?: boolean;
  c3Referral?: boolean;
  aspireReferral?: boolean;
  intMedDirectorReferral?: boolean;
  isAddHomeHealthDiscipRecom?: boolean;
  addHomeHealthDiscipRecom?: boolean;
  addHomeHealthDiscipRecomComment?: string;
  providerNetworkReferral?: boolean;
  statusUpdateProvided?: boolean;
  servicingOrderingProvider?: boolean;
  other?: boolean;
  otherComment?: string;
}

interface OutcomeOfFollow {
  outcome?: string;
  furtherFollowUpComment?: string;
}

interface DecisionOfAorG {
  decision?: string;
  appealOverturned?: string;
  otherComment?: string;
}

export interface AuthorizationPortalNote extends Note {
  memberName: string;
  subscriberNumber: string;
  authorizationNumber: string;
  providerType: string;
  providerName: string;
  visitCount1?: any;
  comment?: any;
  referenceId: string;
  contacts: Contact[];
}

export interface AppealsAndGrievances extends Note {
  issueType?: string;
  urgencyType?: string;
  activitySubType?: string;
  followupWith?: string;
  receiptMethod?: string;
  contacts?: Contact[];
  dateTimeExternalRequestRecieved?: string | Date;
  issueDescription?: string;
  followUpActionsInitiated?: FollowUpActionsInitiated;
  outcomeOfFollow?: OutcomeOfFollow;
  decisionOfAorG?: DecisionOfAorG;
}

export interface PatientSafetyQualityOfCare extends AuthorizationPortalNote {
  referenceClue?: string;
  noteCategoryName?: string;
  issueType?: string;
  activityType?: string;

  receivedType?: string;
  whenReceived?: string;
  identifyingType?: string;
  otherTextIdentify?: string;
  issueDescription?: string;
  issueDescriptionType: IssueDescriptionType;
  actionCompleted: ActionCompleted;

  followUpAction: FollowUpAction;
  followUpActionDescription?: string;
  outcomeOfFollowAction?: string;

}

export interface UMCaseConferenceNote extends AuthorizationPortalNote {
  noteTypeId: string;
  tenantName: string;
  createdOn: string;
  modifiedOn: string;
  dateTime: string;
  careConferenceType: string;
  department: string;
  outreachAttempt: string;
  result: string;
  timeSpentOnCall: string;
}

interface IssueDescriptionType {
  medicationIssues?: string;
  inappropriateTreatment?: string;
  inappropriateLevelOfCare?: string;
  unsafeEnvironment?: string;
  abuseNeglect?: string;
  threatsToSelfOthers?: string;
  delayInCare?: string;
  otherIssueType?: string;
  otherIssueTypeComment?: string;
}
interface ActionCompleted {
  c3Referral?: string;
  aspireReferral?: string;
  followUpPcpServicingProvider?: string;
  followUpPcpMyNEXUSMedicalProvider?: string;
  aspNotificationServicingProvider?: string;
  healthPlanReferral?: string;
  MSWReferral?: string;
  additionalHomeHealthDiscipline?: string;
  providerNetworkReferral?: string;
  careConferenceServicingProvider?: string;
  otherAction?: string;
  otherActionComment?: string;
  additionalDisciplineComment?: string;
  createdOnAction?: string;
}
interface FollowUpAction {
  c3Referral?: string;
  aspireReferral?: string;
  followUpPcpServicingProvider?: string;
  followUpPcpMyNEXUSMedicalProvider?: string;
  aspNotificationServicingProvider?: string;
  healthPlanReferral?: string;
  MSWReferral?: string;
  additionalHomeHealthDiscipline?: string;
  providerNetworkReferral?: string;
  otherAction?: string;
  followUpWithHealthPlan?: string;
  followUpServicingProvider?: string;
  otherActionComment?: string;
  additionalDisciplineComment?: string;
  createdOnAction?: string;
}


export interface IClinicalSolutionsReviewContacts {
  noteType: string;
  communicationType: string;
  dateTimeExternalRequestRecieved: Date | string;
  dateTimeOfOutreach: Date | string;
  outreachComment: string;
  contactName: string;
  contactTitle: string;
  callingEntity: string;
  contactPhone: string;
  extension: string;
  otherContactType?: string;
}

export interface IPeerToPeerSchedulingContacts {
  communicationType: string;
  contactName: string;
  contactTitle: string;
  contactPhone: string;
}

export interface IDmePosProductivity {
  activities: Array<IDmePosActivity>;
  activityTypes: Array<string>;
  otherActivityType: string;
};

export interface IDmePosActivity {
  activityName: string;
  hoursSpend: string;
  minutesSpend: string;
  selectedActivity: boolean
};

export interface IClinicalSolutionsReviewReasonForCall {
  denialExplanation: boolean;
  processEducation: boolean;
  peerToPeerInquiry: boolean;
  messageOrVoicemail: boolean;
  intentToDenyDiscussion: boolean;
  internalConference: boolean;
  peerToPeerOutcome: boolean;
  claims: boolean;
  appealsAndGrievances: boolean;
  clericalError: boolean;
  clericalErrorType: string;
  clericalErrorComment: string;
  other: boolean;
  otherComment: string;
}

export interface IDisciplinesToBeDiscussed {
  name: string;
  selectedDiscipline: boolean;
  serviceLine: string;
}

export interface IClinicalSolutionsReviewActivity {
  name: string;
  selectedActivity: boolean;
  hoursSpend: number;
  minutesSpend: number;
  comment: string;
}

export interface IClinicalSolutionsReview extends Note {
  subType: string;
  primaryDiagnosis: string;
  contacts: IClinicalSolutionsReviewContacts[];
  disciplinesToBeDiscussed: IDisciplinesToBeDiscussed[];
  reasonForCall: IClinicalSolutionsReviewReasonForCall;
  clinicalSummary: string;
  comment: string;
  productivity: IClinicalSolutionsReviewActivity[];
}

export interface IClinicalSolutions extends Note {
  subType: string;
  primaryDiagnosis: string;
  comment: string;
  productivity: IClinicalSolutionsReviewActivity[];
}
export interface IPeerToPeerScheduling extends Note {
  subType: string;
  primaryDiagnosis: string;
  comment: string;
  dateAndTimeInboundOutboundCall: Date | string;
  dateAndTimeP2P: Date | string;
  reasonForPeerToPeer: string;
  otherText1: string;
  otherText2: string;
  callSummary: string;
  providerContactName: string;
  orderingProvider: boolean;
  extensionNumber: string;
  informationReceived: boolean;
  visitsApproved: number;
  visitsUpheld: number;
  contacts: IPeerToPeerSchedulingContacts[];
  disciplinesToBeDiscussed: IDisciplinesToBeDiscussed[];
  productivity: IClinicalSolutionsReviewActivity[];
}
export interface IDmePosPeerToPeerScheduling extends Note {
  additionalDocumentationReceived: string;
  caseCategory: string;
  caseNumber: string;
  callSummary: string;
  comment: string;
  communicationType: string;
  contactName: string;
  contactTitle: string;
  contactPhone: string;
  dateAndTimeInboundOutboundCall: Date | string;
  dateAndTimePeerToPeer: Date | string;
  inboundOutboundCallComment: string;
  peerToPeerComment: string;
  productivity: IDmePosProductivity;
  orderingProviderContactName: string;
  orderingProviderContactPhone: string;
  orderingProviderExtension: string;
  providerRequestingPeerToPeer: string;
  reasonForPeerToPeer: string;
}
export interface IPeerToPeerOutcome extends Note {
  subType: string;
  primaryDiagnosis: string;
  comment: string;
  visitsApproved: number;
  visitsUpheld: number;
  disciplinesToBeDiscussed: IDisciplinesToBeDiscussed[];
}

export interface IDmePosPeerToPeerOutcome extends Note {
  comment: string;
  visitsApproved: number;
  visitsUpheld: number;
  referenceId: number;
}

export interface IClinicalSolutionsReviewReasonForCall {
  denialExplanation: boolean;
  processEducation: boolean;
  peerToPeerInquiry: boolean;
  messageOrVoicemail: boolean;
  intentToDenyDiscussion: boolean;
  internalConference: boolean;
  peerToPeerOutcome: boolean;
  claims: boolean;
  appealsAndGrievances: boolean;
  statusUpdate: boolean;
  dateAdjustment: boolean;
  clericalError: boolean;
  clericalErrorType: string;
  clericalErrorComment: string;
  other: boolean;
  otherComment: string;
}

export interface IDisciplinesToBeDiscussed {
  name: string;
  selectedDiscipline: boolean;
  serviceLine: string;
}

export interface IClinicalSolutionsReviewActivity {
  name: string;
  selectedActivity: boolean;
  hoursSpend: number;
  minutesSpend: number;
  comment: string;
}


export interface IDisciplinesDiscussedItemForm {
  name: FormControl;
  selectedDiscipline: FormControl;
  serviceLine: FormControl;
}

export interface IReasonForCallForm {
  denialExplanation: FormControl;
  processEducation: FormControl;
  peerToPeerInquiry: FormControl;
  messageOrVoicemail: FormControl;
  intentToDenyDiscussion: FormControl;
  internalConference: FormControl;
  peerToPeerOutcome: FormControl;
  claims: FormControl;
  appealsAndGrievances: FormControl;
  statusUpdate: FormControl;
  dateAdjustment: FormControl;
  clericalError: FormControl;
  clericalErrorType: FormControl;
  clericalErrorComment: FormControl;
  other: FormControl;
  otherComment: FormControl;
}

export interface IPacImClinicalSolutionsReviewForm extends INoteForm {
  subType: FormControl;
  primaryDiagnosis: FormControl;
  contacts: FormArray;
  disciplinesToBeDiscussed: FormArray;
  reasonForCall: FormGroup;
  comment: FormControl;
  productivity: FormArray;
}

export interface IClinicalSolutionsReviewForm extends INoteForm {
  subType: FormControl;
  primaryDiagnosis: FormControl;
  contacts: FormArray;
  disciplinesToBeDiscussed: FormArray;
  clinicalSummary: FormControl;
  callSummary: FormControl;
  reasonForCall: FormGroup;
  comment: FormControl;
  productivity: FormArray;
}

export interface IClinicalSolutionsForm extends INoteForm {
  subType: FormControl;
  primaryDiagnosis: FormControl;
  comment: FormControl;
  productivity: FormArray;
}
export interface IPeerToPeerOutcomeForm extends INoteForm {
  subType: FormControl;
  primaryDiagnosis: FormControl;
  disciplinesToBeDiscussed: FormArray;
  comment: FormControl;
  visitsApproved: FormControl;
  visitsUpheld: FormControl;
}

export interface IDmePosPeerToPeerOutcomeForm extends INoteForm {
  modelName: FormControl;
  comment: FormControl;
  visitsApproved: FormControl;
  visitsUpheld: FormControl;
  caseCategory: FormControl;
  caseNumber: FormControl;
}

export interface IPeerToPeerSchedulingForm extends INoteForm {
  subType: FormControl;
  primaryDiagnosis: FormControl;
  disciplinesToBeDiscussed: FormArray;
  dateAndTimeInboundOutboundCall: FormControl;
  dateAndTimeP2P: FormControl;
  reasonForPeerToPeer: FormControl;
  otherText1: FormControl;
  otherText2: FormControl;
  providerContactName: FormControl;
  contactPhoneNumber: FormControl;
  extensionNumber: FormControl;
  orderingProvider: FormControl;
  informationReceived: FormControl;
  callSummary: FormControl;
  contacts: FormArray;
  comment: FormControl;
  visitsApproved: FormControl;
  visitsUpheld: FormControl;
  productivity: FormArray;
}

export interface IDmePosPeerToPeerSchedulingForm extends INoteForm {
  additionalDocumentationReceived: FormControl,
  caseCategory: FormControl,
  caseNumber: FormControl,
  callSummary: FormControl,
  communicationType: FormControl;
  comment: FormControl;
  contactName: FormControl;
  contactTitle: FormControl;
  contactPhone: FormControl;
  dateAndTimeInboundOutboundCall: FormControl;
  dateAndTimePeerToPeer: FormControl;
  inboundOutboundCallComment: FormControl;
  modelName: FormControl;
  peerToPeerComment: FormControl;
  productivity: FormGroup;
  orderingProviderContactName: FormControl;
  orderingProviderContactPhone: FormControl;
  orderingProviderExtension: FormControl;
  providerRequestingPeerToPeer: FormControl;
  reasonForPeerToPeer: FormControl;
}

export interface UrgencyChange extends Note {
  urgencyChange?: string;
  downgradeReason?: string;
  upgradeReason?: string;
}

export interface QualityAudit extends Note {
  comment: string;
}

export interface UrgentNote extends Note {
  contacts?: Contact[];
  authorizationNumber: string;
  memberName: string;
  providerName: string;
  providerType: string;
  referenceClue: string;
  referenceId: string;
  serviceLineSequenceNumber: number;
  serviceRequestType: string;
  subscriberNumber: string;
}

export interface AGFollowup extends Note {
  followupMethod?: string;
  inquiryFollowup?: string;
  followupWith?: string;
  inquiryDetail?: string;
  healthPlanAorGNumber?: string;
  myNexusAorGReferenceNumber?: string;
}

export interface InformationReceived extends Note {
  inquiryMethod?: string;
  responseFromPreviousOutreachAttemp?: string;
  statusOfMember?: string;
  NOMNCIssued?: string;
  NOMNCSignedbyPOA6?: string;
  copyOfPOAAvailable?: string;
  DENCIssue?: string;
  visitDates?: string;
  educatedOn?: string;
}

export interface IAgencyStaffing extends Note {
  memberName: string;
  subscriberNumber: string;
  authorizationNumber: string;
  providerType: string;
  providerName: string;
  providerNPI: string;
  reasonForRefusal: string;
  startOfCareDate: Date | string;
  referenceId: string;
  referenceClue: string;
  otherText1: string;
  providerPhone: string;
  servicingProviderPhone: string;
  providerContactName: string;
  providerAcceptedMember: string;
  contacts?: Contact[];
  extensionNumber: string;
}

export interface NoteWoundAutoFill {
  getAutofillData(index)
  clearAutofillData(index)
  fillAutofillData(index)
  showAutofillButtons(value)
}

export interface IAgencyStaffingForm {
  memberName: FormControl;
  subscriberNumber: FormControl;
  authorizationNumber: FormControl;
  healthPlan: FormControl;
  providerType: FormControl;
  providerName: FormControl;
  providerNPI: FormControl;
  comment: FormControl;
  reasonForRefusal: FormControl;
  startOfCareDate: FormControl;
  referenceId: FormControl;
  referenceClue: FormControl;
  createdBy: FormControl;
  modifiedBy: FormControl;
  otherText1: FormControl;
  noteType: FormControl;
  providerPhone: FormControl;
  servicingProviderPhone: FormControl;
  providerContactName: FormControl;
  providerAcceptedMember: FormControl;
  tenantId: FormControl;
  contacts: FormArray;
  extensionNumber: FormControl;
  noteCategoryName: FormControl;
}

export interface WoundHospitalLE {
  hospitalizationDate: string | null;
  isWoundHospitalLE: boolean | null;
}
export interface WoundErLE {
  erVisitDate: string | null;
  isWoundErLE: boolean | null;
}

export interface IExternalUser {
  accountType: string | null;
  accountTypeId: number;
  centralizedDepartment: string | null;
  createdOn: string;
  displayName: string;
  email: string;
  fax: string;
  firstName: string;
  healthPlans: string[] | null;
  isActive: boolean;
  isApproved: boolean;
  lastName: string;
  modifiedOn: string;
  phone: string;
  phoneExtension: string;
  userId: string;
}

export interface OstomyDocumentation {
  ostomyDocumentationId: string;
  inactivateOstomy: boolean;
  inactivateDate: Date | string | null;
  ostomyType: string | null;
  onsetDate: Date | string | null;
  onsetOfComplications: boolean;
  complicationDetails: string;
  location: string | null;
  abdFeatures: boolean;
  abdFeaturesDetails: string;
  stomaDescription: boolean;
  stomaDescriptionType: string | null;
  effluent: string;
  peristomalSkin: boolean;
  peristomalSkinType: string | null;
  applianceAccessories: string;
  currentWearTime: boolean;
  currentWearTimeDetails: string;
  caregiverIndependent: boolean | null;
  caregiverDetails: string;
  educationReferrals: string;
  locationNote?: LocationNote;
  locationNoteId?: string;
}
